"use client";

import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import { OrganizationSwitcher } from "@cmp-src/organizations/OrganizationSwitcher/OrganizationSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemLink,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  dropdownTriggerVariants
} from "@cmp-src/primitives/dropdown-menu";

import type { AuthenticatedUser } from "@gettactic/api";
import { SETTINGS_ROUTES } from "@gettactic/defines/src/web-routes";
import { buildUrl, cn, useDisclosure } from "@gettactic/helpers";
import { authenticatedUserIsInMoreOrganizations } from "@gettactic/helpers/src/features/authenticatedUserIsInMoreOrganizations";
import { useUnseenFeatures } from "@gettactic/helpers/src/features/useUnseenFeatures";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";
import { invokeMobileAppAction } from "@gettactic/helpers/src/webview/invokeMobileAppAction";
import {
  ZendeskChat,
  type ZendeskChatRef
} from "@gettactic/helpers/src/ZendeskChat";
import { useAuthenticated, useBillingPortal } from "@gettactic/hooks";
import {
  ArrowLeftRight,
  CalendarX,
  CreditCard,
  Eye,
  EyeOff,
  HelpCircle,
  LogOut,
  MessageCircle,
  Settings,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useImpersonateContext } from "@/providers/ImpersonateProvider";

import { MENU_ITEMS, NAV_USER_CLASS_NAMES } from "./defines";
import { LoggedUserNavMenuSeparator } from "./LoggedUserNavMenuSeparator";
import { LoggedUserNavMobileTrigger } from "./LoggedUserNavMobileTrigger";
import { NavUserAvatar } from "./NavUserAvatar";
import { NotificationDot } from "./NotificationDot";
import type { MenuItem, NavUserProps } from "./types";

const isMenuItemEnabled = (
  item: MenuItem,
  user: AuthenticatedUser
): boolean => {
  return item.enabled(user);
};

export const LoggedUserNav: React.FC<NavUserProps> = ({
  user,
  trigger,
  onOpenNewFeatures,
  onOpenCancelReservations
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation("common");
  const {
    canImpersonate,
    isBeingImpersonated,
    toggleSidebarButton,
    isSidebarButtonEnabled,
    setIsWidgetVisible,
    stopImpersonating
  } = useImpersonateContext();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationSwitcherDisclosure = useDisclosure();
  const isAdmin = authenticatedUser?.permissions?.settings ?? false;
  const { hasUnseenFeatures } = useUnseenFeatures({ isAdmin });
  const { openBillingPortal } = useBillingPortal();

  const zendeskChatRef = useRef<ZendeskChatRef>(null);
  const isMobileApp = isTacticAppWebView();
  const [isOpen, setIsOpen] = useState(false);

  // Use ref to track current state to avoid closure issues
  const buttonEnabledRef = useRef(isSidebarButtonEnabled);

  // Update ref when state changes
  useEffect(() => {
    buttonEnabledRef.current = isSidebarButtonEnabled;
  }, [isSidebarButtonEnabled]);

  const handleOpenChat = useCallback(() => {
    zendeskChatRef.current?.openChat();
    setIsOpen(false);
  }, []);

  const handleBillingClick = useCallback(async () => {
    const success = await openBillingPortal();
    if (!success) {
      toast.error("Failed to navigate to the billing portal");
    }
    setIsOpen(false);
  }, [openBillingPortal]);

  const handleImpersonateClick = useCallback(() => {
    toggleSidebarButton();
    setTimeout(() => {
      // Wait to make sure the button is visible before positioning the widget
      if (buttonEnabledRef.current) {
        setIsWidgetVisible(true);
      }
    }, 200);
    setIsOpen(false);
  }, [toggleSidebarButton, setIsWidgetVisible]);

  const handleStopImpersonating = useCallback(() => {
    stopImpersonating();
    setIsOpen(false);
  }, [stopImpersonating]);

  const handleSwitchOrganization = useCallback(() => {
    organizationSwitcherDisclosure.open();
    setIsOpen(false);
  }, [organizationSwitcherDisclosure]);

  const handleMobileAppInfo = useCallback(() => {
    invokeMobileAppAction("openInfo", {
      organizations: authenticatedUser.organizations,
      organization: authenticatedUser?.organization
    });
    setIsOpen(false);
  }, [authenticatedUser.organizations, authenticatedUser?.organization]);

  const handleMobileAppLogout = useCallback(() => {
    invokeMobileAppAction("logout", undefined);
    setIsOpen(false);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpenNewFeatures = useCallback(() => {
    setIsOpen(false);
    onOpenNewFeatures?.();
  }, [onOpenNewFeatures]);

  const handleOpenCancelReservations = useCallback(() => {
    setIsOpen(false);
    onOpenCancelReservations?.();
  }, [onOpenCancelReservations]);

  if (isMobileApp) {
    return (
      <button
        type="button"
        className="relative"
        onClick={() => {
          invokeMobileAppAction("openInfo", {
            organizations: authenticatedUser.organizations,
            organization: authenticatedUser?.organization
          });
        }}
      >
        <LoggedUserNavMobileTrigger
          trigger={trigger}
          name={user.name}
          avatarUrl={user.avatar}
          showNotificationDot={hasUnseenFeatures}
        />
      </button>
    );
  }

  const inMoreOrganizations =
    authenticatedUserIsInMoreOrganizations(authenticatedUser);

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger className={dropdownTriggerVariants.rounded}>
          <LoggedUserNavMobileTrigger
            trigger={trigger}
            name={user.name}
            avatarUrl={user.avatar}
            showNotificationDot={hasUnseenFeatures}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg p-0"
          variant="dropdown"
          side={isMobile ? "bottom" : "right"}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuItemLink
            href={buildUrl(SETTINGS_ROUTES.profile)}
            className={NAV_USER_CLASS_NAMES.menuItem}
            onClick={handleCloseMenu}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <NavUserAvatar name={user.name} avatarUrl={user.avatar} />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
          </DropdownMenuItemLink>
          {!isMobile && <LoggedUserNavMenuSeparator />}
          <DropdownMenuGroup>
            <DropdownMenuItem
              className={cn(
                NAV_USER_CLASS_NAMES.menuItem,
                "flex w-full items-center justify-between",
                hasUnseenFeatures && "font-semibold"
              )}
              onClick={handleOpenNewFeatures}
            >
              <span className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                {t("navActions.newFeatures")}
              </span>
              {hasUnseenFeatures && <NotificationDot className="relative" />}
            </DropdownMenuItem>
            {onOpenCancelReservations && (
              <DropdownMenuItem
                className={cn(
                  NAV_USER_CLASS_NAMES.menuItem,
                  "flex w-full items-center justify-between"
                )}
                onClick={handleOpenCancelReservations}
              >
                <span className="flex items-center gap-2">
                  <CalendarX className="h-4 w-4" />
                  {t("navActions.cancelReservations")}
                </span>
              </DropdownMenuItem>
            )}
            {MENU_ITEMS.map((item) => {
              if (!isMenuItemEnabled(item, authenticatedUser)) return null;

              if (item.isAction && item.id === "chatSupport") {
                return (
                  <DropdownMenuItem
                    key={item.id}
                    className={cn(
                      NAV_USER_CLASS_NAMES.menuItem,
                      !item.showOnMobile &&
                        NAV_USER_CLASS_NAMES.menuItemHiddenOnMobile,
                      "flex w-full items-center justify-between"
                    )}
                    onClick={handleOpenChat}
                  >
                    <span className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4" />
                      {t(`navActions.${item.titleKey}`)}
                    </span>
                  </DropdownMenuItem>
                );
              }
              if (item.isAction && item.id === "billing") {
                return (
                  <DropdownMenuItem
                    key={item.id}
                    className={cn(
                      NAV_USER_CLASS_NAMES.menuItem,
                      !item.showOnMobile &&
                        NAV_USER_CLASS_NAMES.menuItemHiddenOnMobile,
                      "flex w-full items-center justify-between"
                    )}
                    onClick={handleBillingClick}
                  >
                    <span className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      {t(`navActions.${item.titleKey}`)}
                    </span>
                  </DropdownMenuItem>
                );
              }

              const IconComponent = {
                settings: Settings,
                admin: ShieldCheck,
                helpCenter: HelpCircle
              }[item.id];

              return (
                <DropdownMenuItemLink
                  key={item.id}
                  href={item.href || undefined}
                  className={cn(
                    NAV_USER_CLASS_NAMES.menuItem,
                    !item.showOnMobile &&
                      NAV_USER_CLASS_NAMES.menuItemHiddenOnMobile
                  )}
                  onClick={handleCloseMenu}
                >
                  <span className="flex items-center gap-2">
                    {IconComponent && <IconComponent className="h-4 w-4" />}
                    {t(`navActions.${item.titleKey}`)}
                  </span>
                </DropdownMenuItemLink>
              );
            })}
          </DropdownMenuGroup>

          {!isMobileApp && <LoggedUserNavMenuSeparator />}
          {(canImpersonate || isBeingImpersonated) && (
            <DropdownMenuItem
              className={cn(
                NAV_USER_CLASS_NAMES.menuItem,
                "hidden md:flex",
                isBeingImpersonated && "text-red-500"
              )}
              onClick={handleImpersonateClick}
            >
              {isSidebarButtonEnabled ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              {t("navActions.impersonate")}
            </DropdownMenuItem>
          )}
          {isBeingImpersonated && (
            <DropdownMenuItem
              className={cn(
                NAV_USER_CLASS_NAMES.menuItem,
                "flex-1",
                isBeingImpersonated && "text-red-500"
              )}
              onClick={handleStopImpersonating}
            >
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 p-1">
                <div className="flex h-2 w-2 items-center justify-center rounded-[3px] bg-white p-1" />
              </div>
              {t("navActions.stopImpersonating")}
            </DropdownMenuItem>
          )}
          {(isBeingImpersonated || (!isMobile && canImpersonate)) && (
            <LoggedUserNavMenuSeparator />
          )}

          {inMoreOrganizations && isMobile && (
            <>
              <DropdownMenuItemLink
                onClick={handleSwitchOrganization}
                className={NAV_USER_CLASS_NAMES.menuItem}
              >
                <ArrowLeftRight className="h-4 w-4" />
                {t("navActions.switchOrganization")}
              </DropdownMenuItemLink>
              <LoggedUserNavMenuSeparator />
            </>
          )}

          {isMobileApp && (
            <>
              <DropdownMenuItemLink
                onClick={handleMobileAppInfo}
                className={NAV_USER_CLASS_NAMES.menuItem}
              >
                {t("navActions.info")}
              </DropdownMenuItemLink>
              <LoggedUserNavMenuSeparator />
            </>
          )}

          {isMobileApp ? (
            <DropdownMenuItemLink
              className={NAV_USER_CLASS_NAMES.menuItem}
              onClick={handleMobileAppLogout}
            >
              <LogOut />
              {t("navActions.logout")}
            </DropdownMenuItemLink>
          ) : (
            <DropdownMenuItemLink
              className={NAV_USER_CLASS_NAMES.menuItem}
              href={`/auth/logout?returnUrl=${encodeURIComponent(
                `${window.location.origin}?logout=true`
              )}`}
              onClick={handleCloseMenu}
            >
              <LogOut />
              {t("navActions.logout")}
            </DropdownMenuItemLink>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isMobile && inMoreOrganizations && (
        <OrganizationSwitcher
          isOpen={organizationSwitcherDisclosure.isOpen}
          onOpenChange={organizationSwitcherDisclosure.onOpenChange}
        />
      )}
      <ZendeskChat ref={zendeskChatRef} />
    </>
  );
};
